import { apiClient } from "@/services/api/axiosClient";
import type {
  LoginCredentials,
  SignupApiPayload,
  User,
  LoginResponse,
  GoogleLoginPayload,
  AppleLoginPayload,
  SendOtpPayload,
  VerifyOtpPayload,
  SendOtpApiResponse,
  VerifyOtpApiResponse,
  UpdateUserProfileApiResponse,
  UpdateUserProfilePayload,
  ResetPasswordPayload,
} from "./authTypes";
import type { ApiResponse } from "@/services/api/api.types";
import type { LoaderType } from "@/shared/loading";
import type { AxiosRequestConfig } from "axios";
import { tokenService } from "@/services/auth/token.service";
import { API_ENDPOINTS } from "@/services/api/endpoints";
import {
  isUserEligibleForPersistentAuthToken,
  isUserEligibleForApplePersistentAuthToken,
} from "../utils/auth.utils";

function persistAuthTokenIfValid(token: string | undefined, context: string): void {
  if (typeof token !== "string" || token.trim().length === 0) {
    console.error(`[auth] ${context}: refusing to persist empty or invalid token string`);
    return;
  }
  tokenService.setToken(token.trim());
}

function persistRefreshTokenIfPresent(data: unknown): void {
  if (data && typeof data === "object") {
    const raw = data as Record<string, unknown>;
    const refreshToken = raw.refresh_token || raw.refreshToken;
    if (typeof refreshToken === "string" && refreshToken.trim().length > 0) {
      tokenService.setRefreshToken(refreshToken.trim());
    }
  }
}

// Login API response structure matching the actual API format
interface LoginApiResponse {
  status: "success" | "error";
  message: string;
  data: LoginResponse;
}

export const loginApi = async (
  data: LoginCredentials,
  loaderType: LoaderType = "inline",
  elementId?: string
): Promise<User> => {
  const response = await apiClient.post<LoginApiResponse>(API_ENDPOINTS.AUTH.LOGIN, data, {
    metadata: { loaderType, elementId },
  } as AxiosRequestConfig & { metadata?: { loaderType?: LoaderType; elementId?: string } });

  // Extract token and user from response
  const { token, user } = response.data.data;

  // Store token in localStorage

  // tokenService.setToken(token);
  if (isUserEligibleForPersistentAuthToken(user)) {
    persistAuthTokenIfValid(token, "loginApi");
  }
  persistRefreshTokenIfPresent(response.data.data);

  // after extracting token
  sessionStorage.setItem("temp_token", token);

  return user;
};

export const googleLoginApi = async (
  data: GoogleLoginPayload,
  loaderType: LoaderType = "central",
  elementId?: string
): Promise<User> => {
  const response = await apiClient.post<LoginApiResponse>(API_ENDPOINTS.AUTH.GOOGLE_LOGIN, data, {
    metadata: { loaderType, elementId },
  } as AxiosRequestConfig & { metadata?: { loaderType?: LoaderType; elementId?: string } });

  // Extract token and user from response
  const { token, user } = response.data.data;

  // Store token in localStorage
  // tokenService.setToken(token);
  if (isUserEligibleForPersistentAuthToken(user)) {
    persistAuthTokenIfValid(token, "googleLoginApi");
  }
  persistRefreshTokenIfPresent(response.data.data);

  sessionStorage.setItem("temp_token", token);

  return user;
};

export const appleLoginApi = async (
  data: AppleLoginPayload,
  loaderType: LoaderType = "central",
  elementId?: string
): Promise<User> => {
  const response = await apiClient.post<LoginApiResponse>(API_ENDPOINTS.AUTH.APPLE_LOGIN, data, {
    metadata: { loaderType, elementId },
  } as AxiosRequestConfig & { metadata?: { loaderType?: LoaderType; elementId?: string } });

  // Extract token and user from response
  const { token, user } = response.data.data;

  // Store token in localStorage

  // tokenService.setToken(token);
  if (isUserEligibleForApplePersistentAuthToken(user)) {
    persistAuthTokenIfValid(token, "appleLoginApi");
  }
  persistRefreshTokenIfPresent(response.data.data);

  sessionStorage.setItem("temp_token", token);

  return user;
};

export const signupApi = async (
  data: SignupApiPayload,
  loaderType: LoaderType = "inline",
  elementId?: string
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
): Promise<any> => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const response = await apiClient.post<ApiResponse<any>>(API_ENDPOINTS.AUTH.SIGNUP, data, {
    metadata: { loaderType, elementId },
  } as AxiosRequestConfig & { metadata?: { loaderType?: LoaderType; elementId?: string } });
  return response.data;
};

export const resendVerificationApi = async (
  email: string,
  loaderType: LoaderType = "inline",
  elementId?: string
): Promise<void> => {
  await apiClient.post<ApiResponse<void>>(API_ENDPOINTS.AUTH.RESEND_VERIFICATION, { email }, {
    metadata: { loaderType, elementId },
  } as AxiosRequestConfig & { metadata?: { loaderType?: LoaderType; elementId?: string } });
};

export const logoutApi = async (
  token?: string | null,
  refreshToken?: string | null
): Promise<void> => {
  const authToken = token ?? tokenService.getToken();
  const refToken = refreshToken ?? tokenService.getRefreshToken() ?? "";
  await apiClient.post(
    API_ENDPOINTS.AUTH.LOGOUT,
    {
      refresh_token: refToken,
    },
    authToken
      ? {
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        }
      : undefined
  );
};

export const getCurrentUserApi = async (): Promise<User> => {
  const response = await apiClient.get<ApiResponse<User>>(API_ENDPOINTS.AUTH.ME);
  return response.data.data;
};

export const forgotPasswordApi = async (
  email: string,
  loaderType: LoaderType = "inline",
  elementId?: string
): Promise<void> => {
  await apiClient.post<ApiResponse<void>>(API_ENDPOINTS.AUTH.FORGOT_PASSWORD, { email }, {
    metadata: { loaderType, elementId },
  } as AxiosRequestConfig & {
    metadata?: { loaderType?: LoaderType; elementId?: string };
  });
};

export type ContactUsPayload = {
  first_name: string;
  last_name: string;
  email: string;
  phone_number: string;
  sms_consent: boolean;
  email_whatsapp_consent: boolean;
};

export const contactUsApi = async (
  payload: ContactUsPayload,
  loaderType: LoaderType = "inline",
  elementId?: string
): Promise<void> => {
  await apiClient.post<ApiResponse<void>>(API_ENDPOINTS.AUTH.CONTACT_US, payload, {
    metadata: { loaderType, elementId },
  } as AxiosRequestConfig & {
    metadata?: { loaderType?: LoaderType; elementId?: string };
  });
};

export const sendOtpApi = async (
  payload: SendOtpPayload,
  loaderType: LoaderType = "inline",
  elementId?: string
): Promise<SendOtpApiResponse> => {
  const response = await apiClient.post<SendOtpApiResponse>(API_ENDPOINTS.AUTH.SEND_OTP, payload, {
    metadata: { loaderType, elementId },
    headers: {
      "X-API-Key": "andy-internal-api-key-2024-change-in-production",
    },
  } as AxiosRequestConfig & {
    metadata?: { loaderType?: LoaderType; elementId?: string };
  });

  return response.data;
};

export const verifyOtpApi = async (
  payload: VerifyOtpPayload,
  loaderType: LoaderType = "inline",
  elementId?: string
): Promise<VerifyOtpApiResponse> => {
  const response = await apiClient.post<VerifyOtpApiResponse>(
    API_ENDPOINTS.AUTH.VERIFY_OTP,
    payload,
    {
      metadata: { loaderType, elementId },
      headers: {
        "X-API-Key": "andy-internal-api-key-2024-change-in-production",
      },
    } as AxiosRequestConfig & {
      metadata?: { loaderType?: LoaderType; elementId?: string };
    }
  );

  return response.data;
};

// async updateUserProfile(payload: UpdateUserProfilePayload): Promise<UpdateUserProfileApiResponse> {
//   const { data } = await apiClient.put<UpdateUserProfileApiResponse>(
//     "/api/user-service/profile",
//   payload,
// );
// return data;
// },

export const updateUserProfileApi = async (
  payload: UpdateUserProfilePayload,
  loaderType: LoaderType = "inline",
  elementId?: string,
  tempToken?: string
): Promise<UpdateUserProfileApiResponse> => {
  const response = await apiClient.put<UpdateUserProfileApiResponse>(
    API_ENDPOINTS.AUTH.UPDATE_USER_PROFILE,
    payload,
    {
      metadata: { loaderType, elementId },
      headers: {
        ...(tempToken && { Authorization: `Bearer ${tempToken}` }), // ✅ use it
        "X-API-Key": "andy-internal-api-key-2024-change-in-production",
      },
    } as AxiosRequestConfig & {
      metadata?: { loaderType?: LoaderType; elementId?: string };
    }
  );
  return response.data;
};

// async resendVerificationEmail(
//   payload: ResendVerificationEmailPayload,
// ): Promise<ApiResponse<unknown>> {
//   const { data } = await apiClient.post<ApiResponse<unknown>>(
//     "/api/user-service/resend-verification",
//     payload,
//   );
// },

export const resetPasswordApi = async (
  payload: ResetPasswordPayload,
  loaderType: LoaderType = "inline",
  elementId?: string
): Promise<void> => {
  await apiClient.post<ApiResponse<void>>("/api/user-service/reset-password", payload, {
    metadata: { loaderType, elementId },
  } as AxiosRequestConfig & {
    metadata?: { loaderType?: LoaderType; elementId?: string };
  });
};
